import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import "./SearchableSelect.css";

/**
 * A searchable dropdown whose closed state looks identical to a native select.
 * Pass `triggerClassName="bl_compact_select"` to keep the existing design.
 *
 * The open panel is rendered with position:fixed (anchored to the trigger via
 * getBoundingClientRect) so it is never clipped by scrollable/overflow parents.
 */
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "",
  triggerClassName = "",
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState(null);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  const openDropdown = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({ left: rect.left, top: rect.bottom + 4, width: rect.width });
    setQuery("");
    setOpen(true);
  };

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Close on outside click, scroll, or resize (fixed panel would otherwise detach)
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const close = () => setOpen(false);
    // Close when an outside element scrolls (the fixed panel would detach),
    // but ignore scrolling within the dropdown's own options list.
    const handleScroll = (e) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("resize", close, true);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("resize", close, true);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  const filtered = options.filter((o) =>
    (o.label || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <>
      <div
        ref={triggerRef}
        className={triggerClassName}
        onClick={() => (open ? setOpen(false) : openDropdown())}
        style={{
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          minHeight: "32px",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selected ? "#374151" : "#d1d5db",
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
      </div>

      {open &&
        coords &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="searchable_select_dropdown"
            style={{
              position: "fixed",
              left: coords.left,
              top: coords.top,
              width: coords.width,
            }}
          >
            <input
              ref={inputRef}
              className="searchable_select_search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("Rechercher...")}
            />
            <div className="searchable_select_options">
              {filtered.length === 0 ? (
                <div className="searchable_select_empty">
                  {t("Aucun résultat")}
                </div>
              ) : (
                filtered.map((o) => (
                  <div
                    key={o.value}
                    className={`searchable_select_option ${
                      o.value === value ? "active" : ""
                    }`}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    {o.label}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SearchableSelect;
